import type {
  DxfEntityKey,
  DxfMappingValidationCode,
  DxfMappingValidationResult,
} from '@frontend/api';
import { toCamelCase } from '@frontend/utils';
import { createStore, type ReadableStore } from '@frontend/viewer-core';

/**
 * `useValidateDxfSceneMapping`'s settled `data` before any mutation has
 * completed, and the issues/descriptions slice after leaving mapping. A
 * failed check must not reset here — empty issues would look clean and
 * enable Save. Callers treat "no result yet" the same as "checked and clean"
 * for rendering, distinguished instead by `MappingValidationState.isInitialized`.
 */
export const EMPTY_MAPPING_VALIDATION_RESULT: DxfMappingValidationResult = {
  issues: [],
  descriptions: {},
};

export type MappingValidationState = DxfMappingValidationResult & {
  /**
   * A check is currently running — the very first one for these assignments,
   * or a live re-check. Consumers that must never act on stale information
   * (the toolbar's Save gate) block on this, not just on `issues`: a check
   * already in flight might come back with new ones.
   */
  readonly isPending: boolean;
  /**
   * At least one check has completed for the current mapping session.
   * Distinguishes the panel's initial-load spinner (`!isInitialized`) from
   * its "updating" spinner (`isInitialized && isPending`) — `issues.length
   * === 0` cannot do this alone, since a clean mapping and a not-yet-checked
   * one both start there.
   */
  readonly isInitialized: boolean;
  /**
   * The last settled check failed. Kept until a later check succeeds so Save
   * cannot enable on a stale clean result, and the panel can show an error
   * instead of a spinner or "No issues". Abort is not an error.
   */
  readonly error: unknown | null;
};

export const INITIAL_MAPPING_VALIDATION_STATE: MappingValidationState = {
  ...EMPTY_MAPPING_VALIDATION_RESULT,
  isPending: false,
  isInitialized: false,
  error: null,
};

/**
 * Live mapping-validation snapshot plus the one write `useDxfMappingValidation`
 * uses. Its own controller rather than a method on the session factory, matching
 * overlays / mode / tools: the session only wires this in.
 *
 * `set` patches. Unspecified fields stay put, so marking a re-check in flight is
 * `set({ isPending: true })` and does not have to re-specify the last issues.
 * `createStore` already no-ops when the recipe returns the same reference; a
 * patch always allocates, so a redundant `set({ isPending: true })` will notify.
 */
export type MappingValidationManager = ReadableStore<MappingValidationState> & {
  set(patch: Partial<MappingValidationState>): void;
};

export const createMappingValidationController =
  (): MappingValidationManager => {
    const store = createStore(INITIAL_MAPPING_VALIDATION_STATE);
    return {
      getState: store.getState,
      subscribe: store.subscribe,
      set(patch) {
        store.setState((previous) => ({ ...previous, ...patch }));
      },
    };
  };

/**
 * One issue *type* present in the mapping, with every entity that
 * participates in it — the design groups by type rather than listing one row
 * per backend occurrence (see the Figma "Issues" panel).
 *
 * Entities are deduped and unioned across every occurrence of this code: two
 * separate `bend_not_straight_line` contours, say, land in one group.
 */
export type RawMappingIssueGroup = {
  readonly id: DxfMappingValidationCode;
  readonly message: string;
  readonly entityKeys: readonly DxfEntityKey[];
};

export type MappingIssueEntityRow = {
  readonly key: DxfEntityKey;
  readonly label: string;
  readonly isSelectable: boolean;
  readonly isSelected: boolean;
};

/**
 * `RawMappingIssueGroup` plus everything that needs the model and the current
 * selection: per-entity labels, visibility, and which entities/groups already
 * read as selected.
 */
export type MappingIssueGroup = Omit<RawMappingIssueGroup, 'entityKeys'> & {
  /** `entities.length`, surfaced separately so the row doesn't recompute it. */
  readonly count: number;
  readonly isSelected: boolean;
  readonly isSelectable: boolean;
  readonly entities: readonly MappingIssueEntityRow[];
};

/**
 * Groups the backend's flat, per-occurrence issues by `code`.
 *
 * This is the code/message/entity-union part only — it has no model or
 * selection access, so it can't yet say which entities are *visible* or
 * *selected*, or what to label each one with (entity type + role). See
 * `useDxfMappingIssueGroups`, which adds that and is what the panel actually
 * renders from.
 *
 * Message copy comes from the backend's own `descriptions` (`dxf/constants.py`'s
 * `MAPPING_VALIDATION_MESSAGES`) rather than a frontend copy of the same
 * table, so the two can't drift apart. A code missing from `descriptions`
 * (shouldn't happen — the backend sends one for every code it emits) falls
 * back to the raw code rather than rendering blank.
 *
 * Lookup is by camelCase: `getPayload` runs `keysToCamelCaseDeep` on the
 * response, so description *keys* arrive as `bendNotStraightLine` while
 * `issue.code` values stay snake_case (`bend_not_straight_line`). Looking up
 * with the raw code would always miss and show the key as the label.
 */
export const groupMappingValidationIssues = ({
  issues,
  descriptions,
}: DxfMappingValidationResult): readonly RawMappingIssueGroup[] => {
  const order: DxfMappingValidationCode[] = [];
  const keysByCode = new Map<DxfMappingValidationCode, Set<DxfEntityKey>>();

  issues.forEach(({ code, entityKeys }) => {
    let keys = keysByCode.get(code);
    if (!keys) {
      keys = new Set();
      keysByCode.set(code, keys);
      order.push(code);
    }
    entityKeys.forEach((key) => keys?.add(key));
  });

  return order.map((code) => ({
    id: code,
    // Prefer camelCase (runtime API path); raw code covers direct/test payloads.
    message: descriptions[toCamelCase(code)] ?? descriptions[code] ?? code,
    entityKeys: [...(keysByCode.get(code) ?? [])],
  }));
};
